import { getSalesAnalysis } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    /** YYYY-MM of the cell that opened the modal. Null when modal closed. */
    month: null,
    /** Account label clicked in the table (e.g. "Productos"). Null = month total. */
    account: null,
    /** Categoría dinámica activa para stores_distribution (tiendas/marcas/...). */
    groupBy: null,
    /**
     * Filtro multi-select por categoría: { tiendas: ["T1","T2"], marcas: [...] }.
     * Categoría sin entries (o no listada) = sin filtro para esa dimensión.
     * Recorta headline + stores + ranking. KPIs lo ignoran por contrato.
     */
    selectedItems: {},
    /**
     * Full payload returned by the backend. Frozen to opt out of Vue 2's
     * deep observer.
     * @type {{
     *   month: string,
     *   account: string | null,
     *   period_label: string,
     *   headline: { amount: number, mom: number | null, yoy: number | null,
     *               breakdown: Array<{ account: string, amount: number }> },
     *   clients_kpis: Array<{ metric: string, value: number,
     *                         format: "currency" | "count",
     *                         mom: number | null, yoy: number | null }>,
     *   stores_distribution: Array<{ name: string, amount: number, share: number }>,
     *   clients_ranking: Array<{ name: string, amount: number, share: number }>,
     * } | null}
     */
    data: null,
    isLoading: false,
    error: null,
    /** Free-text filter for the clients ranking card (case-insensitive). */
    clientsQuery: "",
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * Clients ranking filtered by `clientsQuery`. Returns the full list when
     * the query is empty.
     */
    filteredClients: (state) => {
        const ranking = state.data?.clients_ranking ?? [];
        const q = state.clientsQuery.trim().toLowerCase();
        if (!q) return ranking;
        return ranking.filter((c) => c.name.toLowerCase().includes(q));
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_CONTEXT(state, { month, account, groupBy, selectedItems }) {
        state.month = month;
        state.account = account ?? null;
        state.groupBy = groupBy ?? null;
        // Asignación frozen para evitar mutaciones accidentales desde el caller
        // y porque el shape es pequeño — no necesitamos reactividad profunda.
        state.selectedItems = Object.freeze(selectedItems ?? {});
    },
    SET_SELECTED_ITEMS(state, selectedItems) {
        state.selectedItems = Object.freeze(selectedItems ?? {});
    },
    SET_DATA(state, payload) {
        state.data = payload;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CLIENTS_QUERY(state, query) {
        state.clientsQuery = query;
    },
    RESET(state) {
        state.month = null;
        state.account = null;
        state.groupBy = null;
        state.selectedItems = {};
        state.data = null;
        state.error = null;
        state.clientsQuery = "";
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchSalesAnalysis({ commit }, { month, account = null, groupBy = null, selectedItems = null }) {
        commit("SET_CONTEXT", { month, account, groupBy, selectedItems });
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await getSalesAnalysis(month, account, groupBy, selectedItems);
            commit("SET_DATA", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesAnalysis]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar el análisis de ventas."));
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
